import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Field, reduxForm, SubmissionError } from "redux-form";
import { UserCog, Save, X } from "lucide-react";
import { createPartner, updatePartner, fetchPartner } from "../actions/partners";
import { fetchCustomersName } from "../actions/customers";
import LoadingProgress from "../components/LoadingProgress";
import { renderInputField, renderSelectField, renderCheckboxField } from "../utils/forms";

class PartnerForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditing: false,
      partnerId: null,
    };
  }

  componentDidMount() {
    const { editingPartner, match, fetchPartner } = this.props;
    // Note: fetchCustomersName is called by the parent component (PartnersView)
    // so we don't need to call it here

    // If editingPartner is passed as prop, use it directly (no need to fetch)
    if (editingPartner) {
      this.setState({ isEditing: true, partnerId: editingPartner._id });
      // Don't fetch again since we already have the data
    } else if (match && match.params && match.params.id) {
      // Only fetch if using route params (standalone page)
      this.setState({ isEditing: true, partnerId: match.params.id });
      fetchPartner(match.params.id);
    }
  }

  componentDidUpdate(prevProps) {
    const { partner, partnersNeedRefresh } = this.props;

    if (partnersNeedRefresh && !prevProps.partnersNeedRefresh) {
      if (this.state.partnerId) {
        this.props.fetchPartner(this.state.partnerId);
      }
    }
  }

  submit = (values) => {
    const { createPartner, updatePartner, history, onSubmit } = this.props;
    const { isEditing, partnerId } = this.state;

    return new Promise((resolve, reject) => {
      const action = isEditing
        ? updatePartner(partnerId, values)
        : createPartner(values);

      action.then(() => {
        if (onSubmit) {
          onSubmit();
        } else {
          history.push('/partners');
        }
        resolve();
      }).catch((error) => {
        reject(new SubmissionError({ _error: error.message || 'Error saving partner' }));
      });
    });
  };

  render() {
    const { loading, error, role, partner, customers } = this.props;
    const { isEditing } = this.state;

    // Only admins can access this form
    if (role !== "ADMIN") {
      return (
        <div className="container mt-4">
          <div className="alert alert-danger">
            No tienes permisos para acceder a esta sección.
          </div>
        </div>
      );
    }

    if (loading && isEditing) {
      return <LoadingProgress />;
    }

    if (error && isEditing) {
      return (
        <div className="container mt-4">
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            {error}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => this.props.fetchPartner(this.state.partnerId)}
          >
            <i className="fas fa-sync mr-2"></i>
            Reintentar
          </button>
        </div>
      );
    }

    const initialValues = isEditing && partner ? {
      name: partner.name || '',
      email: partner.email || '',
      phone: partner.phone || '',
      city: partner.city || '',
      partnerType: partner.partnerType || 'OTHER',
      isActive: partner.isActive !== undefined ? partner.isActive : true,
      customer_id: partner.customer_id?._id || partner.customer_id || '',
    } : {
      name: '',
      email: '',
      phone: '',
      city: '',
      partnerType: 'OTHER',
      isActive: true,
      customer_id: '',
    };

    return (
      <div className=" mt-4">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title mb-0">
                  <UserCog size={24} className="mr-2" />
                  {isEditing ? 'Editar Partner' : 'Nuevo Partner'}
                </h4>
              </div>
              <div className="card-body">
                <form onSubmit={this.props.handleSubmit(this.submit)}>
                  <div className="row">
                    <div className="col-md-6">
                      <Field
                        name="name"
                        component={renderInputField}
                        label="Nombre *"
                        placeholder="Nombre del partner"
                      />
                    </div>

                    <div className="col-md-6">
                      <Field
                        name="email"
                        component={renderInputField}
                        type="email"
                        label="Email *"
                        placeholder="email@ejemplo.com"
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <Field
                        name="phone"
                        component={renderInputField}
                        label="Teléfono"
                        placeholder="+34 600 000 000"
                      />
                    </div>

                    <div className="col-md-6">
                      <Field
                        name="city"
                        component={renderInputField}
                        label="Ciudad"
                        placeholder="Madrid"
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <Field
                        name="partnerType"
                        component={renderSelectField}
                        label="Tipo de Partner"
                      >
                        <option value="OTHER">Otro</option>
                        <option value="SUPPLIER">Proveedor</option>
                        <option value="DISTRIBUTOR">Distribuidor</option>
                        <option value="RESELLER">Revendedor</option>
                        <option value="SERVICE_PROVIDER">Proveedor de Servicios</option>
                      </Field>
                    </div>

                    <div className="col-md-6">
                      <Field
                        name="customer_id"
                        component={renderSelectField}
                        label="Cliente *"
                      >
                        <option value="">Seleccionar cliente...</option>
                        {customers && customers.filter(customer => customer.Type === "GENERAL").sort((a, b) => a.Fullname.localeCompare(b.Fullname)).map(customer => (
                          <option key={customer._id} value={customer._id}>
                            {customer.Fullname}
                          </option>
                        ))}
                      </Field>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <Field
                        name="isActive"
                        component={renderCheckboxField}
                        label="Partner activo"
                      />
                    </div>
                  </div>

                  {this.props.error && (
                    <div className="alert alert-danger">
                      <i className="fas fa-exclamation-triangle mr-2"></i>
                      {this.props.error}
                    </div>
                  )}

                  <div className="form-group mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary mr-2"
                      disabled={this.props.submitting}
                    >
                      <Save size={16} className="mr-2" />
                      {this.props.submitting ? 'Guardando...' : (isEditing ? 'Actualizar Partner' : 'Crear Partner')}
                    </button>
                    {this.props.onCancel ? (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={this.props.onCancel}
                      >
                        <X size={16} className="mr-2" />
                        Cancelar
                      </button>
                    ) : (
                      <Link to="/partners" className="btn btn-secondary">
                        <X size={16} className="mr-2" />
                        Cancelar
                      </Link>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const validate = (values) => {
  const errors = {};
  if (!values.name) errors.name = 'El nombre es requerido';
  if (!values.email) errors.email = 'El email es requerido';
  if (!values.customer_id) errors.customer_id = 'El cliente es requerido';
  return errors;
};

const mapStateToProps = (state, ownProps) => {
  const isEditing = ownProps.editingPartner || (ownProps.match && ownProps.match.params && ownProps.match.params.id);
  // Use editingPartner directly if available, otherwise look it up in state
  const partner = ownProps.editingPartner || (isEditing ? state.partners.partners.find(p => p._id === (ownProps.match && ownProps.match.params && ownProps.match.params.id)) : state.partners.partner);

  const initialValues = isEditing && partner ? {
    name: partner.name || '',
    email: partner.email || '',
    phone: partner.phone || '',
    city: partner.city || '',
    partnerType: partner.partnerType || 'OTHER',
    isActive: partner.isActive !== undefined ? partner.isActive : true,
    customer_id: partner.customer_id?._id || partner.customer_id || '',
  } : {
    name: '',
    email: '',
    phone: '',
    city: '',
    partnerType: 'OTHER',
    isActive: true,
    customer_id: '',
  };

  return {
    initialValues,
    auth: state.auth,
    role: state.auth.role,
    customers: state.customers.customers,
    partner,
    partnersNeedRefresh: state.partners.partnersNeedRefresh,
    loading: state.partners.loading,
    error: state.partners.error,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createPartner,
      updatePartner,
      fetchPartner,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'partnerForm',
    validate,
    enableReinitialize: true
  })(PartnerForm)
);